import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

const control =
  'block w-full rounded-xl border bg-white px-3 text-sm text-slate-900 shadow-xs placeholder:text-slate-400 transition-all ' +
  'hover:border-slate-300 focus:outline-none focus:ring-4 focus:ring-brand-500/15 focus:border-brand-400 disabled:bg-slate-50 disabled:text-slate-500';

const borderFor = (error?: string) => (error ? 'border-red-400' : 'border-slate-200/90');

interface FieldShellProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  id: string;
  children: ReactNode;
  className?: string;
  required?: boolean;
}

export function FieldShell({ label, hint, error, id, children, className, required }: FieldShellProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-slate-700">
          {label}
          {required && (
            <span className="text-accent-500 ml-0.5" aria-hidden>
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

type Common = { label?: ReactNode; hint?: ReactNode; error?: string; wrapperClassName?: string };

function describedBy(id: string, error?: string, hint?: ReactNode) {
  return error ? `${id}-error` : hint ? `${id}-hint` : undefined;
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & Common>(
  function Input({ label, hint, error, wrapperClassName, className, id: idProp, required, ...rest }, ref) {
    const auto = useId();
    const id = idProp ?? auto;
    return (
      <FieldShell
        label={label}
        hint={hint}
        error={error}
        id={id}
        className={wrapperClassName}
        required={required}
      >
        <input
          ref={ref}
          id={id}
          required={required}
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy(id, error, hint)}
          className={cn(control, borderFor(error), 'h-10', className)}
          {...rest}
        />
      </FieldShell>
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & Common>(
  function Select(
    { label, hint, error, wrapperClassName, className, id: idProp, children, required, ...rest },
    ref,
  ) {
    const auto = useId();
    const id = idProp ?? auto;
    return (
      <FieldShell
        label={label}
        hint={hint}
        error={error}
        id={id}
        className={wrapperClassName}
        required={required}
      >
        <select
          ref={ref}
          id={id}
          required={required}
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy(id, error, hint)}
          className={cn(control, borderFor(error), 'h-10 pr-8', className)}
          {...rest}
        >
          {children}
        </select>
      </FieldShell>
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & Common>(
  function Textarea({ label, hint, error, wrapperClassName, className, id: idProp, required, ...rest }, ref) {
    const auto = useId();
    const id = idProp ?? auto;
    return (
      <FieldShell
        label={label}
        hint={hint}
        error={error}
        id={id}
        className={wrapperClassName}
        required={required}
      >
        <textarea
          ref={ref}
          id={id}
          required={required}
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy(id, error, hint)}
          className={cn(control, borderFor(error), 'py-2', className)}
          {...rest}
        />
      </FieldShell>
    );
  },
);

export const Checkbox = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }
>(function Checkbox({ label, description, className, id: idProp, ...rest }, ref) {
  const auto = useId();
  const id = idProp ?? auto;
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className="text-brand-600 accent-brand-600 mt-0.5 h-4 w-4 rounded border-slate-300"
        {...rest}
      />
      <label htmlFor={id} className="text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        {description && <span className="block text-xs text-slate-500">{description}</span>}
      </label>
    </div>
  );
});
